import { HttpErrorResponse } from '@angular/common/http';
import { TimeoutError } from 'rxjs';
import { AppError, AppErrorKind } from './app-error.model';

type ErrorCopy = Omit<AppError, 'kind' | 'status'>;

const DEFAULT_SUBJECT = 'la información';

const SUBJECT_AGNOSTIC_COPY: Record<'network' | 'unknownDescription', string> = {
  network:
    'No pudimos comunicarnos con el servicio. Revisa tu conexión y vuelve a cargar la página.',
  unknownDescription:
    'Ocurrió un problema inesperado al procesar la consulta. Vuelve a cargar la página.',
};

export function buildErrorCatalog(
  subject: string = DEFAULT_SUBJECT,
): Record<AppErrorKind, ErrorCopy> {
  return {
    timeout: {
      tone: 'error',
      title: 'La consulta demoró más de lo esperado',
      description: `El servicio no respondió a tiempo. Vuelve a cargar la página para consultar ${subject}.`,
    },
    network: {
      tone: 'error',
      title: 'Sin conexión con el servicio',
      description: SUBJECT_AGNOSTIC_COPY.network,
    },
    unauthorized: {
      tone: 'warning',
      title: 'Tu sesión expiró',
      description: `Vuelve a iniciar sesión para continuar consultando ${subject}.`,
    },
    forbidden: {
      tone: 'warning',
      title: 'No tienes acceso a esta información',
      description: `Tu usuario no está habilitado para consultar ${subject} de este convenio. Contacta al administrador de tu institución.`,
    },
    notFound: {
      tone: 'warning',
      title: `No encontramos ${subject}`,
      description:
        'Puede que todavía no esté disponible para tu institución. Si crees que es un error, contacta al administrador de tu institución.',
    },
    rateLimited: {
      tone: 'warning',
      title: 'Demasiadas consultas seguidas',
      description:
        'Alcanzaste el límite de consultas permitidas, un resguardo de la información. Espera unos minutos antes de volver a consultar.',
    },
    server: {
      tone: 'error',
      title: 'El servicio no está disponible',
      description: `Ocurrió un problema al cargar ${subject}. Vuelve a cargar la página en unos minutos.`,
    },
    unknown: {
      tone: 'error',
      title: `No pudimos cargar ${subject}`,
      description: SUBJECT_AGNOSTIC_COPY.unknownDescription,
    },
  };
}

export const ERROR_CATALOG: Record<AppErrorKind, ErrorCopy> = buildErrorCatalog();

function kindFromStatus(status: number): AppErrorKind {
  if (status === 0) return 'network';
  // 408 lo emite el TimeoutInterceptor del BFF; 504 es un timeout de la API
  // aguas abajo. Ambos son la misma cosa para el usuario que el TimeoutError
  // del cliente, y no un "error inesperado".
  if (status === 408 || status === 504) return 'timeout';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 429) return 'rateLimited';
  if (status >= 500) return 'server';
  return 'unknown';
}

export function toAppError(cause: unknown, subject?: string): AppError {
  const catalog = buildErrorCatalog(subject);

  if (cause instanceof TimeoutError) {
    return { kind: 'timeout', ...catalog.timeout };
  }

  if (cause instanceof HttpErrorResponse) {
    const kind = kindFromStatus(cause.status);
    return { kind, ...catalog[kind], status: cause.status };
  }

  return { kind: 'unknown', ...catalog.unknown };
}
